<template>
    <vx-expandable-card v-if="team.team_attributes?.contact_groups" data-test="org-unit-coverage-card">
        <template v-slot:header>Organizational Units</template>
        <template v-slot:content>
            <div class="flex flex-col gap-4">
                <vx-radio-selection
                    v-model="data.org_unit_coverage"
                    :disabled="!props.canEdit"
                    aria-label="Organizational unit coverage"
                >
                    <vx-radio-selection-item value="none" data-test="coverage-none">
                        <template v-slot:header>No OUs</template>
                        <template v-slot:content>Every group accepts through its own owner.</template>
                    </vx-radio-selection-item>
                    <vx-radio-selection-item value="all" data-test="coverage-all">
                        <template v-slot:header>All OUs</template>
                        <template v-slot:content>
                            Each OU owner accepts once for every group in their OU. Groups outside an OU accept through
                            their own owner.
                        </template>
                    </vx-radio-selection-item>
                    <vx-radio-selection-item value="selected" data-test="coverage-selected">
                        <template v-slot:header>Selected OUs</template>
                        <template v-slot:content>
                            Only the OUs you pick accept on behalf of their groups. Every other group accepts through
                            its own owner.
                        </template>
                    </vx-radio-selection-item>
                </vx-radio-selection>

                <div v-if="data.org_unit_coverage === 'selected'">
                    <vx-label>Covered OUs</vx-label>
                    <vx-selectable
                        v-model="data.org_units"
                        :options="options"
                        :loading="searching"
                        :disabled="!props.canEdit"
                        :error="displayFirstError(validation.org_units.$errors)"
                        @search="searchOrgUnits"
                        multiple
                        label="name"
                        size="lg"
                        :relativeDropdown="true"
                        :closeOnSelect="false"
                        placeholder="Search OUs"
                        data-test="coverage-org-units"
                    ></vx-selectable>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import {
    VxExpandableCard,
    VxLabel,
    VxRadioSelection,
    VxRadioSelectionItem,
    VxSelectable,
} from '@voxie/frontend-components';
import { onMounted, ref, watch } from 'vue';
import { helpers, requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { adminOrgUnitsClient } from '~/services';
import { useTeamDetails } from '~/components/kiosk/kiosk-teams/composables/useTeamDetails';
import { useToasted } from '~/composables/useToasted';
import debounce from '~/utils/debounce';
import { catchRequestError } from '~/utils/request';
import { displayFirstError } from '~/utils/validation';
import type { TermFormData, TermOrgUnit } from '~/services/adminTermsClient.types';

const props = defineProps<{
    teamId: number;
    canEdit: boolean;
}>();

const data = defineModel<TermFormData>({ required: true });

const toasted = useToasted();
const { mount, team } = useTeamDetails();
const options = ref<TermOrgUnit[]>([]);
const searching = ref(false);

const searchOrgUnits = debounce((query: string = '') => {
    searching.value = true;

    adminOrgUnitsClient
        .listOrgUnits({
            team_id: props.teamId,
            query: query || undefined,
            sort_field: 'name',
            sort_dir: 'asc',
            per_page: 100,
        })
        .then(({ data: response }: { data: { data: TermOrgUnit[] } }) => {
            options.value = response.data.map(({ id, name }) => ({ id, name }));
        })
        .catch((error: unknown) => toasted.error(catchRequestError(error)))
        .finally(() => {
            searching.value = false;
        });
});

const validation = useVuelidate(
    {
        org_units: {
            required: helpers.withMessage(
                'Pick at least one OU.',
                requiredIf(
                    () =>
                        Boolean(team.value.team_attributes?.contact_groups) &&
                        data.value.org_unit_coverage === 'selected'
                )
            ),
        },
    },
    data
);

watch(
    () => data.value.org_unit_coverage,
    (coverage) => {
        if (coverage === 'selected' && options.value.length === 0) {
            searchOrgUnits();
        }
    },
    { immediate: true }
);

onMounted(() => mount(props.teamId));
</script>
